import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, MapPin, ChevronRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, FaqBlock, CtaBand, Breadcrumbs } from "@/components/Blocks";
import {
  SERVICES, getCity, getService, comboContent, comboAnchor,
  breadcrumbLd, faqLd, serviceLd,
} from "@/data/contentEngine";

const ComboPage = () => {
  const { serviceSlug, citySlug } = useParams();
  const service = getService(serviceSlug);
  const city = getCity(citySlug);
  if (!service || !city) return <Navigate to="/" replace />;

  const c = comboContent(city, service);
  const nearby = city.nearby.map((s) => getCity(s)).filter(Boolean);
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      serviceLd(service, city.name),
      breadcrumbLd([
        { label: "Home", to: "/" },
        { label: "Services", to: "/services" },
        { label: service.title, to: `/services/${service.slug}` },
        { label: city.name, to: `/services/${service.slug}/${city.slug}` },
      ]),
      faqLd(c.faqs),
    ],
  };

  return (
    <>
      <Seo title={c.title} description={c.description} path={`/services/${service.slug}/${city.slug}`} jsonLd={jsonLd} />
      <PageHero
        eyebrow={`${service.title} · ${city.county}`}
        title={c.h1}
        sub={c.intro}
        image={service.image}
        testId="combo-hero"
      />
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-12" data-testid="combo-content">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Services", to: "/services" },
            { label: service.title, to: `/services/${service.slug}` },
            { label: city.name, to: null },
          ]}
        />
        <div className="mt-10 grid grid-cols-1 gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHead eyebrow={city.name} title={c.localHeading} testId="combo-local-heading" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate-300">{c.local}</p>
            </Reveal>

            <Reveal delay={0.12}>
              <h2 className="mt-12 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="combo-delivery-heading">
                {c.deliveryHeading}
              </h2>
            </Reveal>
            <div className="mt-6 space-y-6">
              {c.paras.map((p, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <p className="text-base leading-relaxed text-slate-300">{p}</p>
                </Reveal>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {service.deliverables.map((d, i) => (
                <Reveal key={d} delay={i * 0.04}>
                  <div className="flex items-start gap-3 rounded-sm border border-slate-800 bg-obsidian-900 px-4 py-3" data-testid={`combo-deliverable-${i}`}>
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    <span className="text-sm text-slate-300">{d}</span>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <h2 className="mt-12 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="combo-coverage-heading">
                {service.title} coverage across {city.name}
              </h2>
            </Reveal>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="combo-hubs-list">
              {city.hubs.map((h) => (
                <span key={h} className="rounded-sm border border-slate-700 bg-obsidian-900 px-3 py-1.5 text-xs text-slate-300">
                  {h}
                </span>
              ))}
            </div>
            <Reveal delay={0.1}>
              <p className="mt-8 text-base leading-relaxed text-slate-300">{c.closing}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <h2 className="mt-14 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="combo-faq-heading">
                {service.title} in {city.name} — FAQs
              </h2>
            </Reveal>
            <div className="mt-4">
              <FaqBlock faqs={c.faqs} testId="combo-faq" />
            </div>
          </div>

          <aside className="space-y-6">
            <Reveal>
              <div className="rounded-md border border-slate-800 bg-obsidian-900 p-6" data-testid="combo-other-services-panel">
                <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
                  More security services in {city.name}
                </h2>
                <ul className="mt-4 space-y-1.5">
                  {otherServices.map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/services/${s.slug}/${city.slug}`}
                        data-testid={`combo-other-service-${s.slug}`}
                        className="group flex items-center justify-between rounded-sm px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-obsidian-800 hover:text-amber-400"
                      >
                        {comboAnchor(s, city)}
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-700 group-hover:text-amber-500" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="rounded-md border border-slate-800 bg-obsidian-900 p-6" data-testid="combo-nearby-panel">
                <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
                  {service.title} nearby
                </h2>
                <ul className="mt-4 space-y-1.5">
                  {nearby.map((n) => (
                    <li key={n.slug}>
                      <Link
                        to={`/services/${service.slug}/${n.slug}`}
                        data-testid={`combo-nearby-${n.slug}`}
                        className="group flex items-center justify-between rounded-sm px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-obsidian-800 hover:text-amber-400"
                      >
                        <span className="flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 text-slate-600 group-hover:text-amber-500" />
                          {service.title} in {n.name}
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-700 group-hover:text-amber-500" />
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/cities/${city.slug}`}
                  data-testid="combo-city-hub-link"
                  className="mt-5 flex items-center justify-between rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm font-semibold text-amber-400 transition-colors hover:bg-amber-500/20"
                >
                  All security services in {city.name}
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
        <CtaBand
          title={`${service.title} for your ${city.name} premises`}
          sub="Free risk assessment, transparent quotation and rapid deployment — speak to our Northern Ireland control room today."
        />
      </section>
    </>
  );
};

export default ComboPage;
